import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";

export default function SouthIndian10() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "10");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="X4ciKMX8SYw"
        title="Kalayil Dhinamum — A.R. Rahman · New (2004)"
        caption="Watch the right hand in any piano cover. The middle three fingers are barely moving — the outer two are doing everything. That's finger independence in action."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/10-kalayil-dhinamum-page1.png"
        caption="Jason's chart — the opening in E minor with its first melody notes; the chords Em · A · Em · Bm Em and Em · Am Em · Em · Bm Em; the scale E F♯ G A B D with the 6th as either C (minor 6th) or C♯ (major 6th); and a left hand in open fifths (E–B, F♯–C♯, G–D, A–E, B–F♯, C–G, D–A)."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/10-kalayil-dhinamum-page2.png"
        caption="Page 2 — the opening piano figure written out (E B E F♯ …, E B G D …) and Jason's ways of grouping its eight notes: 3+3+2, 2+3+3, 3+2+3."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/10-kalayil-dhinamum-page3.png"
        caption="Page 3 — the bridge: Em D · C G · Am D · Am D, with the melody notes written above."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/10-kalayil-dhinamum-page4.png"
        caption="Page 4 — a section in C♯ minor: C♯m (i) · B (♭VII) · A (♭VI) · A♭7 (= G♯7, the V7) → C♯m, with the melody's scale degrees and note names."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/10-kalayil-dhinamum-page5.png"
        caption="Page 5 — the C♯ minor scale Jason calls a 'hybrid': B♮ from natural minor and B♯ (written C) from harmonic minor, over E · A♭7 · C♯m · B · C♯m."
      />

      <SISCard label="The big idea" title="Finger independence — the technique hiding inside the song">
        <p>
          The opening piano figure of <em>Kalayil Dhinamum</em> sits on E and seems simple on the surface — an endless stream of 8th notes in E minor. Jason calls it <em>"like a tongue twister exercise for piano."</em> But when you break down what each finger is actually doing, you find a masterclass in <strong>finger independence</strong>: the ability to hold some fingers still while others move freely.
        </p>
        <p>
          The right-hand framework: <strong>thumb (1) + middle finger (3) + pinky (5)</strong> hold the chord structure — E, G, B — while the <strong>index (2) and ring finger (4)</strong> weave a melody between them. The weaving fingers play F♯ (between 1 and 3) and A (between 3 and 5). The result sounds like two voices happening simultaneously: a held harmonic foundation and a singing melodic line.
        </p>
        <p>
          The song also varies its 6th degree. As Jason puts it: <em>"If it is major sixth we call the scale as Dorian. If it's a minor sixth, we call the scale as Aeolian, also known as natural minor."</em> That Dorian/Aeolian alternation runs alongside the finger technique. Two lessons in one passage.
        </p>
      </SISCard>

      <SISCard label="The modal switch" title="C♯ vs C♮ — one note, two worlds">
        <p>
          In E minor, the 6th scale degree is either C♯ (Dorian, the raised 6th) or C♮ (Aeolian / natural minor, the ♭6). Rahman alternates between these two throughout the song — using each for a different section or phrase.
        </p>
        <p>
          The <strong>E Dorian</strong> version (with C♯) belongs to the Carnatic raga <strong>Kharaharapriya</strong>. It sounds warmer, almost optimistic despite being in minor. The IV chord built on A is major (A–C♯–E), which brightens the harmony considerably.
        </p>
        <p>
          The <strong>E Aeolian</strong> version (with C♮) belongs to <strong>Natabhairavi</strong>. It sounds darker, more resigned. The chord built on A becomes minor (A–C–E). Jason's chart shows both: Em → A → Em in one line, Em → Am → Em in the next.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="E Dorian — Kharaharapriya (C♯, raised 6th) · from Jason's chart"
        caption="The C♯ in the A major chord (A–C♯–E) is the Dorian fingerprint. It brightens the harmony — the IV chord is major, a colour natural minor doesn't have."
        chords={[
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3","E4"] },
          { bar: true },
          { chord: "A", rn: "IV", function: "Pre-dominant", pitches: ["A3","C#4","E4","A4"] },
          { bar: true },
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3","E4"] },
          { bar: true },
          { chord: "Bm", rn: "v", function: "Dominant", pitches: ["B3","D4","F#4"] },
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3"] },
        ]}
      />

      <RomanNumeralAnalysis
        label="E Aeolian — Natabhairavi (C♮, ♭6) · from Jason's chart"
        caption="Now the chord on A is minor: Am (A–C–E) carries C♮, the ♭6 of E minor. Same tonic, same bass motion — one note changes the colour."
        chords={[
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3","E4"] },
          { bar: true },
          { chord: "Am", rn: "iv", function: "Pre-dominant", pitches: ["A3","C4","E4","A4"] },
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3","E4"] },
          { bar: true },
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3","E4"] },
          { bar: true },
          { chord: "Bm", rn: "v", function: "Dominant", pitches: ["B3","D4","F#4"] },
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3"] },
        ]}
      />

      <SISInfluence
        artists={[
          "Bach — Two-Part Inventions (independent lines in each hand)",
        ]}
      >
        <p>
          Bach wrote his fifteen Two-Part Inventions as teaching pieces: each gives the two hands their own independent line. Practising one alongside this song builds the same control of independent fingers and hands.
        </p>
      </SISInfluence>

      <SISCallout label="Practice drill — the weaving fingers">
        <p>
          Place your right hand with: thumb on E, middle on G, pinky on B (the E above middle C works well). Press all three down gently and hold. Now, <em>without lifting the held notes</em>, play F♯ with your index finger (between thumb and middle). Then play A with your ring finger (between middle and pinky). Build a repeating pattern: E–F♯–G–A–G–F♯–E, all with fingers 1–2–3–4–3–2–1, while 1, 3, and 5 stay anchored. When this feels comfortable, try C♯ instead of C♮ in an Am voicing above — hear the Dorian version. Then C♮ for the Aeolian version.
        </p>
      </SISCallout>

      <SISTakeaway>
        <p>
          Finger independence is not a trick — it's the fundamental difference between playing <em>chords</em> and playing <em>voices</em>. When your fingers are truly independent, a single hand can sustain a harmonic foundation while simultaneously singing a melody above it. That's the piano's unique power. Great pianists of every style rely on this principle. The reason musicians practise scales and exercises isn't to get faster; it's to make each finger capable of thinking for itself. Once your fingers are independent, your music gains counterpoint for free.
        </p>
      </SISTakeaway>
    </LessonShell>
  );
}
